/**
 * Nút "Tiếp tục với Google", dùng chung cho màn đăng nhập và màn đăng ký.
 *
 * Hai màn làm cùng một việc: mở cửa sổ Google, lấy ID token, hỏi máy chủ xem
 * email này đã có tài khoản chưa. Khác nhau chỉ ở việc làm gì với câu trả lời,
 * nên phần đó để màn hình truyền vào.
 *
 * Hook cũng lo nốt đường đi vòng: trình duyệt chặn cửa sổ bật lên thì Firebase
 * phải rời trang, và khi quay lại thì kết quả chỉ đọc được bằng
 * getRedirectResult - mỗi màn có nút này đều phải đọc, nếu không người dùng
 * quay lại và thấy đúng màn cũ như chưa làm gì.
 */
import { useEffect, useRef, useState } from "react";

import * as SBService from "../helpers/sportbookService";
import { apiErrorMessage } from "../helpers/apiError";
import { bocThanTraVe } from "../helpers/authSession";
import { dangNhapGoogle, ketQuaChuyenHuong, thongDiepLoiGoogle, HoSoGoogle } from "../helpers/googleAuth";

/** Hồ sơ Google kèm những gì máy chủ biết thêm về nó. */
export interface GoogleCanDangKy {
  idToken: string;
  email: string;
  name: string | null;
  picture: string | null;
}

interface ThamSo {
  /** Email đã có tài khoản: `than` là thân đăng nhập ({ token, refreshToken }). */
  onDangNhap: (than: any) => void;
  /** Chưa có tài khoản: còn thiếu số điện thoại, phải đi tiếp luồng đăng ký. */
  onCanDangKy: (hoSo: GoogleCanDangKy) => void;
  /** Lỗi đã dịch sẵn thành câu tiếng Việt để màn hình hiện lên. */
  onLoi: (thongDiep: string) => void;
  /** Bật/tắt vòng xoay chờ của màn hình. */
  setChoDoi?: (dangCho: boolean) => void;
}

export const useGoogleSignIn = ({ onDangNhap, onCanDangKy, onLoi, setChoDoi }: ThamSo) => {
  const [dangChay, setDangChay] = useState(false);

  /* Giữ callback trong ref: màn hình truyền vào hàm mới mỗi lần render, mà
     effect đọc kết quả chuyển hướng chỉ được chạy đúng một lần lúc mở màn. */
  const xuLy = useRef({ onDangNhap, onCanDangKy, onLoi, setChoDoi });
  xuLy.current = { onDangNhap, onCanDangKy, onLoi, setChoDoi };

  const dungHoSo = async (hoSo: HoSoGoogle) => {
    const { onDangNhap: dangNhap, onCanDangKy: canDangKy, onLoi: loi, setChoDoi: cho } = xuLy.current;
    try {
      cho?.(true);
      setDangChay(true);
      const than = bocThanTraVe(await SBService.googleLogin(hoSo.idToken));

      if (than?.needRegister) {
        canDangKy({
          idToken: hoSo.idToken,
          // Lấy theo máy chủ trước: đó là email đọc từ token đã được Google ký.
          email: than.profile?.email ?? hoSo.email,
          name: than.profile?.name ?? hoSo.name,
          picture: than.profile?.picture ?? hoSo.picture,
        });
      } else {
        dangNhap(than);
      }
    } catch (e: any) {
      loi(apiErrorMessage(e));
    } finally {
      cho?.(false);
      setDangChay(false);
    }
  };

  useEffect(() => {
    let conSong = true;
    ketQuaChuyenHuong()
      .then((kq) => {
        if (conSong && kq.trangThai === "ok") {
          void dungHoSo(kq.hoSo);
        }
      })
      .catch(() => undefined);

    return () => {
      conSong = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bamGoogle = async () => {
    const { onLoi: loi, setChoDoi: cho } = xuLy.current;
    // Dọn thông báo lỗi của lần trước: bấm lại mà câu lỗi cũ còn nằm đó thì
    // người dùng tưởng lần này cũng hỏng.
    loi("");
    try {
      cho?.(true);
      setDangChay(true);
      const kq = await dangNhapGoogle();
      if (kq.trangThai === "ok") {
        await dungHoSo(kq.hoSo);

        return;
      }
      /* 'huy' = người dùng tự đóng cửa sổ, 'dangChuyenHuong' = trang sắp bị
         thay: cả hai đều không phải lỗi nên không hiện gì. */
    } catch (e: any) {
      // thongDiepLoiGoogle đọc mã lỗi của Firebase và nói thẳng chỗ cần sửa:
      // ba trong số các lỗi hay gặp nhất là cấu hình thiếu bên Firebase Console,
      // bấm lại bao nhiêu lần cũng vẫn hỏng.
      loi(thongDiepLoiGoogle(e));
    } finally {
      cho?.(false);
      setDangChay(false);
    }
  };

  return { bamGoogle, dangChay };
};
